"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useRouter = exports.Router = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const react_use_error_boundary_1 = require("react-use-error-boundary");
const useListener_1 = require("../../hooks/useListener/useListener");
const dom_1 = require("../../helpers/dom/dom");
const react_2 = require("../../helpers/react/react");
exports.Router = (0, react_2.createContextWithHook)(({ localization, redirects, routes }) => {
    const { listen } = (0, useListener_1.useListener)();
    const [anchor, setAnchor] = (0, react_1.useState)(({ target: '' }));
    const [location, setLocation] = (0, react_1.useState)({ hash: window.location.hash, queries: {}, route: window.location.pathname });
    const [renderer, setRenderer] = (0, react_1.useState)(null);
    const getRoute = (0, react_1.useCallback)((path) => {
        const chunks = path.split('/').filter(Boolean);
        return chunks.length === 0 ? redirects?.['/'] : Object.entries(redirects || {}).reduce((route, [from, to]) => {
            const chunksFrom = from.split('/').filter(Boolean);
            const chunksTo = to.split('/').filter(Boolean);
            const keys = Object.keys(Array.from({ length: Math.max(chunks.length, chunksFrom.length, chunksTo.length) })).map((key) => parseInt(key));
            const matches = keys.map((key) => {
                const candidate = chunksFrom[key];
                if (candidate && candidate.startsWith(':'))
                    return chunks[key];
                if (candidate === chunks[key])
                    return chunksTo[key];
                return null;
            });
            return matches.some((chunk) => !chunk) ? route : `/${matches.join('/')}`;
        }, redirects?.[path] || path);
    }, [redirects]);
    const match = (0, react_1.useCallback)(() => {
        const chunks = location.route.split('/');
        return Object.keys(routes).reduce((template, route) => {
            const parameters = {};
            const candidate = route.split('/').map((chunk, index) => {
                if (chunk.startsWith(':')) {
                    parameters[chunk] = chunks[index];
                    return parameters[chunk];
                }
                return chunk;
            }).join('/');
            return candidate === location.route ? { route, parameters } : template;
        }, { route: null, parameters: {} });
    }, [location, routes]);
    const navigate = (0, react_1.useCallback)((path) => {
        const target = path.split('#');
        const route = getRoute(target[0]) || '/';
        const hash = target[1] ? `#${target[1]}` : '';
        const queries = Object.fromEntries(window.location.search.substring(1).split('&').filter(Boolean).map((query) => query.split('=')));
        setLocation({ hash, route, queries });
    }, [getRoute]);
    const render = (0, react_1.useCallback)(() => {
        const { route, parameters } = match();
        if (location.route !== window.location.pathname || location.hash !== window.location.hash) {
            window.history.pushState({}, '', `${location.route}${location.hash}`);
        }
        const element = (0, react_use_error_boundary_1.withErrorBoundary)(() => (!(0, react_use_error_boundary_1.useErrorBoundary)()[0] ? (0, react_1.createElement)(routes[route], { parameters }) : (0, jsx_runtime_1.jsx)("span", { children: localization?.error })));
        setAnchor({ target: `${location.hash.substring(1)}` });
        setRenderer(route ? (0, react_1.createElement)(element) : (0, react_1.createElement)('div', {}, localization?.unknown || ''));
    }, [localization, location, routes, match]);
    const triggerNavigateOnMount = (0, react_1.useCallback)(() => navigate(`${window.location.pathname}${window.location.hash}`), [navigate]);
    const triggerNavigateOnPrevious = (0, react_1.useCallback)(() => listen('popstate', () => navigate(`${window.location.pathname}${window.location.hash}`)), [listen, navigate]);
    const triggerRenderOnLocationChange = (0, react_1.useCallback)(() => render(), [render]);
    const triggerScrollOnAnchorChange = (0, react_1.useCallback)(() => (anchor.target
        ? setTimeout(() => (0, dom_1.scrollToElement)(document.getElementById(anchor.target)), 200)
        : setTimeout(() => (0, dom_1.scrollToElement)(document.body), 0)), [anchor]);
    (0, react_1.useEffect)(() => { triggerNavigateOnMount(); }, [triggerNavigateOnMount]);
    (0, react_1.useEffect)(() => { triggerNavigateOnPrevious(); }, [triggerNavigateOnPrevious]);
    (0, react_1.useEffect)(() => { triggerRenderOnLocationChange(); }, [triggerRenderOnLocationChange]);
    (0, react_1.useEffect)(() => { triggerScrollOnAnchorChange(); }, [triggerScrollOnAnchorChange]);
    return (0, react_1.useMemo)(() => ({
        location,
        navigate,
        renderer,
    }), [
        location,
        navigate,
        renderer,
    ]);
});
const useRouter = () => (0, react_1.useContext)(exports.Router.Context);
exports.useRouter = useRouter;
//# sourceMappingURL=useRouter.js.map